import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import {
  getAnswerableQuestions,
  getParentSerial,
} from '../../SubmissionView/submissionResponseUtils';
import LeftIcon from '../../../static/img/darkgray16leftarrow.svg';
import RightIcon from '../../../static/img/darkgray16rightarrow.svg';
import { FeedbackContext } from '../FeedbacksRoot/FeedbackContext';
import QuestionNavigationPanel from '../QuestionNavigationPanel';

const FeedbackQuestionSlider = ({
  setQuestionIndex,
  QuestionIndex,
  questions,
  submission,
  isTeacher = true,
  isTeacherUploadMode = false,
}) => {
  const { isResetEditorTextSelection } = React.useContext(FeedbackContext);
  const history = useHistory();
  const location = useLocation();

  const isParentWithChildren = (index) => {
    const question = questions[index];
    if (!question) return false;

    const hasChildren = questions.some((q) => {
      const parent = q.parentQuestionSerialNumber ?? q.parentSerialNumber;
      return (
        parent != null &&
        parent !== '' &&
        String(parent) === String(question.serialNumber)
      );
    });

    return hasChildren;
  };

  const findNextNavigableIndex = (currentIndex, direction) => {
    let nextIndex = currentIndex + direction;

    while (nextIndex >= 0 && nextIndex < questions.length) {
      if (!isParentWithChildren(nextIndex)) {
        return nextIndex;
      }
      nextIndex += direction;
    }

    return currentIndex;
  };

  const navigableMeta = useMemo(() => {
    const list = questions || [];
    const answerable = getAnswerableQuestions(list);
    const currentSerial = list[QuestionIndex]?.serialNumber;
    const position =
      answerable.findIndex(
        (q) => String(q.serialNumber) === String(currentSerial),
      ) + 1;
    return {
      total: answerable.length || list.length || 1,
      position: position > 0 ? position : QuestionIndex + 1,
    };
  }, [questions, QuestionIndex]);

  const handlePreviousQuestion = () => {
    const nextIndex = findNextNavigableIndex(QuestionIndex, -1);
    if (nextIndex !== QuestionIndex) {
      setQuestionIndex(nextIndex);
      isResetEditorTextSelection();

      const nextQuestion = questions[nextIndex];
      if (nextQuestion) {
        const params = new URLSearchParams(location.search);
        params.set('question', nextQuestion.serialNumber);
        history.push({ pathname: location.pathname, search: `?${params.toString()}` });
      }
    }
  };

  const handleNextQuestion = () => {
    const nextIndex = findNextNavigableIndex(QuestionIndex, 1);
    if (nextIndex !== QuestionIndex) {
      setQuestionIndex(nextIndex);
      isResetEditorTextSelection();

      const nextQuestion = questions[nextIndex];
      if (nextQuestion) {
        const params = new URLSearchParams(location.search);
        params.set('question', nextQuestion.serialNumber);
        history.push({ pathname: location.pathname, search: `?${params.toString()}` });
      }
    }
  };

  const handleQuestionSelect = (index) => {
    let targetIndex = index;
    let targetSerial = questions[index]?.serialNumber;

    if (isParentWithChildren(index)) {
      const parentSerial = questions[index].serialNumber;
      const firstChild = questions.find((q) => {
        const parent = getParentSerial(q);
        return String(parent) === String(parentSerial);
      });
      if (firstChild) {
        const childIndex = questions.findIndex(
          (q) => q.serialNumber === firstChild.serialNumber,
        );
        if (childIndex >= 0) {
          targetIndex = childIndex;
          targetSerial = firstChild.serialNumber;
        }
      }
    }

    setQuestionIndex(targetIndex);
    isResetEditorTextSelection();

    if (targetSerial) {
      const params = new URLSearchParams(location.search);
      params.set('question', targetSerial);
      history.push({
        pathname: location.pathname,
        search: `?${params.toString()}`,
      });
    }
  };

  return (
    <QuestionCounter size={questions.length}>
      {questions.length > 1 && (
        <QuestionBtn
          onClick={handlePreviousQuestion}
          disabled={findNextNavigableIndex(QuestionIndex, -1) === QuestionIndex}
        >
          <img src={LeftIcon} alt="" /> Previous
        </QuestionBtn>
      )}
      {isTeacherUploadMode ? (
        <QuestionBox>
          Question {navigableMeta.position} of {navigableMeta.total}
        </QuestionBox>
      ) : (
        <QuestionNavigationPanel
          questions={questions}
          currentQuestionIndex={QuestionIndex}
          onQuestionSelect={handleQuestionSelect}
          submission={submission}
          isTeacher={isTeacher}
        />
      )}
      {questions.length > 1 && (
        <QuestionBtn
          onClick={handleNextQuestion}
          disabled={findNextNavigableIndex(QuestionIndex, 1) === QuestionIndex}
        >
          Next <img src={RightIcon} alt="" />
        </QuestionBtn>
      )}
    </QuestionCounter>
  );
};

export default FeedbackQuestionSlider;

export const QuestionCounter = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  padding: 0;
  background-color: rgba(255, 255, 255, 1);
  max-width: 100%;
  margin: 0;
  flex: 0 1 auto;
  min-width: 0;
`;

export const QuestionBtn = styled.button`
  min-width: 85px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 16px;
  border: 1.5px solid rgba(75, 70, 79, 1);
  font-family: var(--font-family-ibm_plex_sans);
  font-weight: 600;
  font-size: var(--font-size-s);
  line-height: 20px;
  color: rgba(75, 70, 79, 1);
  background-color: transparent;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`;

export const QuestionBox = styled.div`
  font-family: var(--font-family-ibm_plex_sans);
  font-weight: 600;
  font-size: var(--font-size-s);
  line-height: 20px;
  color: rgba(75, 70, 79, 1);
  white-space: nowrap;
`;
